import { useState, useEffect, useMemo } from 'react'

// Par-picker for manual-mode rounds. The user just told us they want to play
// a venue neither provider has, so we have no par data — this modal lets them
// fill it from the scorecard at the cart shed before teeing off.
//
// Two paths supported:
//  - Total par: type one number (e.g. "72"), tap Auto-distribute → spreads as
//    evenly as possible across the hole count using 3 / 4 / 5.
//  - Per-hole: tap 3 / 4 / 5 on each row directly. Total recomputes live.
//
// Default = all par 4 (matches the manual course stub's initial seed). Save
// returns the array; the caller persists via db('update_course_pars', ...).
export default function ParPickerModal({ open, onClose, onSave, holesCount = 18, initialPars = null }) {
  const [pars, setPars] = useState(() => Array.from({ length: holesCount }, (_, i) => initialPars?.[i] ?? 4))
  const [totalInput, setTotalInput] = useState('')

  // Reset when the modal opens or hole count changes — keeps the state in
  // sync with the parent's currently-selected hole count.
  useEffect(() => {
    if (!open) return
    setPars(Array.from({ length: holesCount }, (_, i) => initialPars?.[i] ?? 4))
    setTotalInput('')
  }, [open, holesCount])
  // initialPars excluded intentionally — we only want this to re-init on open.
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const total = useMemo(() => pars.reduce((s, p) => s + (p || 0), 0), [pars])

  // Auto-distribute: given a target total, spread (target - 4*N) as +1 / -1
  // adjustments across holes, alternating to spread them out rather than
  // clumping. e.g. par 72 / 18 holes → 4 par-3s + 4 par-5s + 10 par-4s,
  // interleaved every few holes.
  const autoDistribute = () => {
    const target = parseInt(totalInput, 10)
    if (!Number.isFinite(target)) return
    const min = 3 * holesCount, max = 5 * holesCount
    const clamped = Math.max(min, Math.min(max, target))
    const base = Array(holesCount).fill(4)
    let diff = clamped - 4 * holesCount
    const direction = diff > 0 ? 1 : -1
    diff = Math.abs(diff)
    // Spread the +1s or -1s evenly: pick every floor(N / diff)-th hole.
    if (diff > 0) {
      const stride = Math.max(1, Math.floor(holesCount / diff))
      let placed = 0
      // Start at the middle of the front nine, then walk outward by stride.
      // Keeps the difficulty distribution roughly even rather than dumping
      // all the 5s on the front or back.
      const order = []
      for (let i = 0; i < holesCount; i++) {
        order.push((Math.floor(holesCount / 2) + i * stride) % holesCount)
      }
      for (const idx of order) {
        if (placed >= diff) break
        const newVal = base[idx] + direction
        if (newVal >= 3 && newVal <= 5) {
          base[idx] = newVal
          placed++
        }
      }
    }
    setPars(base)
  }

  const setPar = (i, val) => {
    setPars(prev => {
      const next = [...prev]
      next[i] = val
      return next
    })
  }

  if (!open) return null

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 9999,
      padding: 0,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--green-dark)', color: 'var(--cream)',
        width: '100%', maxWidth: 480, maxHeight: '90vh',
        borderTopLeftRadius: 12, borderTopRightRadius: 12,
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.5)',
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid var(--green-mid)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Course Par</h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: 'var(--gray-300)' }}>
              Type a total + auto-distribute, or tap each hole.
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: 'var(--gray-300)',
            fontSize: '1.5rem', cursor: 'pointer', padding: '0 4px',
          }}>×</button>
        </div>

        <div style={{ padding: '12px 18px', display: 'flex', gap: 10, alignItems: 'center', borderBottom: '1px solid var(--green-mid)' }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--gray-300)' }}>Total</label>
          <input
            type="number" inputMode="numeric"
            value={totalInput} onChange={(e) => setTotalInput(e.target.value)}
            placeholder={String(total)}
            style={{
              flex: 1, padding: '8px 10px', fontSize: '0.95rem',
              background: 'var(--green-deep)', color: 'var(--cream)',
              border: '1px solid var(--green-mid)', borderRadius: 'var(--radius)',
            }}
          />
          <button onClick={autoDistribute} disabled={!totalInput} style={{
            padding: '8px 12px', fontSize: '0.78rem', fontWeight: 600,
            background: totalInput ? 'var(--gold)' : 'var(--green-mid)',
            color: totalInput ? 'var(--green-deep)' : 'var(--gray-300)',
            border: 'none', borderRadius: 'var(--radius)',
            cursor: totalInput ? 'pointer' : 'not-allowed',
          }}>Auto-distribute</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 18px' }}>
          {pars.map((p, i) => {
            const holeNum = i + 1
            const isFront = holeNum <= 9
            const showFrontHeader = i === 0
            const showBackHeader = i === 9 && holesCount > 9
            return (
              <div key={i}>
                {showFrontHeader && (
                  <p style={{ margin: '6px 0 4px', fontSize: '0.66rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                    Front 9
                  </p>
                )}
                {showBackHeader && (
                  <p style={{ margin: '10px 0 4px', fontSize: '0.66rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                    Back 9
                  </p>
                )}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0',
                  borderBottom: i < pars.length - 1 ? '1px solid var(--green-mid)' : 'none',
                }}>
                  <span style={{ width: 28, fontFamily: 'var(--font-mono)', fontSize: '0.85rem', color: 'var(--gray-300)' }}>
                    {holeNum}
                  </span>
                  <div style={{ display: 'flex', gap: 6, flex: 1, justifyContent: 'flex-end' }}>
                    {[3, 4, 5].map(v => (
                      <button
                        key={v}
                        onClick={() => setPar(i, v)}
                        style={{
                          width: 40, padding: '7px 0', fontSize: '0.9rem', fontWeight: 700,
                          background: p === v ? 'var(--gold)' : 'var(--green-deep)',
                          color: p === v ? 'var(--green-deep)' : 'var(--cream)',
                          border: `1px solid ${p === v ? 'var(--gold)' : 'var(--green-mid)'}`,
                          borderRadius: 'var(--radius)', cursor: 'pointer',
                        }}
                      >{v}</button>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--green-mid)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
          <div style={{ fontSize: '0.85rem', color: 'var(--cream)' }}>
            <span style={{ color: 'var(--gray-300)' }}>Total:</span>{' '}
            <strong style={{ fontFamily: 'var(--font-mono)', fontSize: '1rem' }}>{total}</strong>
          </div>
          <button onClick={() => onSave(pars)} style={{
            padding: '10px 18px', fontSize: '0.9rem', fontWeight: 700,
            background: 'var(--gold)', color: 'var(--green-deep)',
            border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer',
          }}>Save pars</button>
        </div>
      </div>
    </div>
  )
}
